import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { HistoricNarService } from "../../../../../api/services/historicNar/historicNarService";
import type { HistoricNarRecommendation } from "../../../../../api/services/historicNar/types";
import { HistoricNarIngestionState } from "../../../../../enums/historicNarIngestionState";
import { logError } from "../../../../../utils/customLogger";
import type { AutomationSettings } from "../components/historicNarAutomationFields";
import { getSourceSuggestions } from "../utils/historicNarSourceUtils";

interface UseConfirmSourceIngestOptions {
  row?: HistoricNarRecommendation;
  onClose: () => void;
  onIngested: () => void;
}

/** Own the confirm-source form lifecycle and ingest submission. */
const useConfirmSourceIngest = ({
  row,
  onClose,
  onIngested,
}: UseConfirmSourceIngestOptions) => {
  const ingestRequest = useRef<AbortController>();
  const sourceFilePath = useMemo(
    () =>
      row?.identifier
        ? `auth_grid/historic_nar/${encodeURIComponent(row.identifier)}/${uuidv4()}.pdf`
        : undefined,
    [row?.identifier],
  );
  const title = useMemo(() => {
    switch (row?.status) {
      case HistoricNarIngestionState.INGESTED_MANUAL:
      case HistoricNarIngestionState.TOUCHLESS:
      case HistoricNarIngestionState.AGENTIC:
        return "Edit";
      case HistoricNarIngestionState.NEEDS_REVIEW:
        return "Re-Ingest";
      default:
        return "Confirm source & ingest";
    }
  }, [row?.status]);

  // Up to two source suggestions (first source_text entries, dx code stripped)
  // offered as click-to-fill chips; the first also seeds the field.
  const suggestions = useMemo(
    () => getSourceSuggestions(row?.volume_analytics?.source_text, 2),
    [row],
  );

  const [sourceText, setSourceText] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<string>("");
  const [hasSelectedFile, setHasSelectedFile] = useState(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [neverExpire, setNeverExpire] = useState<boolean>(false);
  const [agenticAutomation, setAgenticAutomation] =
    useState<AutomationSettings>({
      enabled: false,
      selectedValues: [],
    });
  const [touchlessAutomation, setTouchlessAutomation] =
    useState<AutomationSettings>({
      enabled: false,
      selectedValues: [],
    });

  const confirmedSource = sourceText.trim();
  const hasMissingAutomationSelection = [
    agenticAutomation,
    touchlessAutomation,
  ].some(
    ({ enabled, selectedValues }) => enabled && selectedValues.length === 0,
  );

  // Reset for a new recommendation only; metadata refreshes must preserve edits.
  useEffect(
    () => {
      setSourceText(suggestions[0]?.source ?? "");
      setUploadedFile("");
      setHasSelectedFile(false);
      setNeverExpire(false);
      // Pre-fill each automation section from the row's recorded client lists.
      const agenticClients = row?.agentic_enabled_for ?? [];
      const touchlessClients = row?.touchless_enabled_for ?? [];
      setAgenticAutomation({
        enabled: agenticClients.length > 0,
        selectedValues: agenticClients,
      });
      setTouchlessAutomation({
        enabled: touchlessClients.length > 0,
        selectedValues: touchlessClients,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row?.identifier],
  );

  useEffect(
    () => () => {
      ingestRequest.current?.abort();
    },
    [],
  );

  const handleIngest = async () => {
    if (
      !row ||
      !confirmedSource ||
      (hasSelectedFile && !uploadedFile) ||
      hasMissingAutomationSelection
    )
      return;
    ingestRequest.current?.abort();
    const controller = new AbortController();
    ingestRequest.current = controller;
    setIsSaving(true);
    try {
      const response = await HistoricNarService.ingestRecommendation(
        {
          identifier: row.identifier,
          source_text: confirmedSource,
          source_file: uploadedFile || undefined,
          never_expire: neverExpire,
          // Disabled modes must remain explicit in the wire payload and clear any
          // selections retained locally for a later toggle-on.
          automation: {
            agent_client_ids: agenticAutomation.enabled
              ? agenticAutomation.selectedValues
              : [],
            touchless_client_ids: touchlessAutomation.enabled
              ? touchlessAutomation.selectedValues
              : [],
          },
        },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      if (response.success) {
        const match = response.grid_match;
        toast.success(
          match === "linked"
            ? "Linked to existing Auth Grid row"
            : "Ingested to Auth Grid",
        );
        onIngested();
        onClose();
      } else {
        toast.error(response.message || "Failed to ingest recommendation");
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      logError(error as Error, "Error ingesting historic NAR recommendation");
      toast.error("Error ingesting recommendation");
    } finally {
      if (!controller.signal.aborted) setIsSaving(false);
      if (ingestRequest.current === controller) {
        ingestRequest.current = undefined;
      }
    }
  };

  return {
    title,
    isSaving,
    disableSave:
      isSaving ||
      !confirmedSource ||
      (hasSelectedFile && !uploadedFile) ||
      hasMissingAutomationSelection,
    handleIngest,
    source: {
      sourceText,
      suggestions,
      onChange: (value: string) => setSourceText(value),
    },
    payerPdf: {
      filePath: sourceFilePath,
      onUploadedUrlChange: (url: string) => setUploadedFile(url),
      onFileSelectionChange: (selected: boolean) =>
        setHasSelectedFile(selected),
    },
    expiration: {
      neverExpire,
      onChange: (value: boolean) => setNeverExpire(value),
    },
    automation: {
      agenticAutomation,
      touchlessAutomation,
      onAgenticChange: (settings: AutomationSettings) =>
        setAgenticAutomation(settings),
      onTouchlessChange: (settings: AutomationSettings) =>
        setTouchlessAutomation(settings),
    },
  };
};

export default useConfirmSourceIngest;
